import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAllAlertSettings, updateAlertSettings, fetchAllThresholds, updateThresholds } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AlertSettingsOverview.css';

const AlertSettingsOverview = () => {
  const navigate = useNavigate();
  const { authData } = useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingThresholds, setLoadingThresholds] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingChanges, setPendingChanges] = useState(new Map()); // Track unsaved alert settings changes
  const [pendingThresholdChanges, setPendingThresholdChanges] = useState(new Map()); // Track unsaved threshold changes
  const [pendingScheduleChanges, setPendingScheduleChanges] = useState(new Map()); // Track unsaved schedule changes
  const [savingAll, setSavingAll] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null); // Success/error message
  const [activeTab, setActiveTab] = useState('alerts'); // 'alerts', 'thresholds', or 'schedule'
  const [thresholds, setThresholds] = useState([]);

  // Alert type labels and their keys
  const alertTypes = [
    { key: 'cpu', label: 'CPU' },
    { key: 'memory', label: 'Memory' },
    { key: 'disk', label: 'Disk' },
    { key: 'responseTime', label: 'Response Time' },
    { key: 'loadAverage', label: 'Load Average' },
    { key: 'lastPayment', label: 'Last Payment' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'heartbeat', label: 'Heartbeat' },
    { key: 'camera', label: 'Camera' },
    { key: 'gate', label: 'Gate' },
    { key: 'controller', label: 'Controller' },
    { key: 'myParkPayment', label: 'MyPark Payment' },
    { key: 'myparkServer', label: 'MyPark Server' },
    { key: 'pinpadStatus', label: 'Pinpad Status' },
    { key: 'dbAvailability', label: 'DB Availability' },
    { key: 'windowsFirewall', label: 'Windows Firewall' },
    { key: 'siteIpChanged', label: 'Site IP Changed' },
    { key: 'unrecognizedExits', label: 'Unrecognized Exits' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'thresholds' && thresholds.length === 0) {
      loadThresholds();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllAlertSettings();
      setSettings(data);
    } catch (err) {
      console.error('Failed to load alert settings:', err);
      setError('Failed to load alert settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadThresholds = async () => {
    try {
      setLoadingThresholds(true);
      setError(null);
      const data = await fetchAllThresholds();
      // Filter to only monitored sites (same as alert settings)
      // If settings are loaded, filter by them, otherwise show all
      if (settings.length > 0) {
        const monitoredSiteIds = new Set(settings.map(s => s.siteId));
        const filteredData = data.filter(t => monitoredSiteIds.has(t.siteId));
        setThresholds(filteredData);
      } else {
        // If settings not loaded yet, just set all thresholds
        setThresholds(data);
      }
    } catch (err) {
      console.error('Failed to load thresholds:', err);
      setError('Failed to load thresholds. Please try again.');
    } finally {
      setLoadingThresholds(false);
    }
  };

  const handleToggle = (siteId, alertKey) => {
    const siteSettings = settings.find(s => s.siteId === siteId);
    if (!siteSettings) return;

    const newValue = !siteSettings[alertKey];
    
    // Optimistically update UI
    setSettings(prev => prev.map(s => 
      s.siteId === siteId ? { ...s, [alertKey]: newValue } : s
    ));

    // Track pending changes
    setPendingChanges(prev => {
      const newMap = new Map(prev);
      const changeKey = `${siteId}-${alertKey}`;
      newMap.set(changeKey, { siteId, alertKey, newValue, oldValue: siteSettings[alertKey] });
      return newMap;
    });
  };

  const handleSaveAll = async () => {
    const hasAlertChanges = pendingChanges.size > 0;
    const hasThresholdChanges = pendingThresholdChanges.size > 0;
    const hasScheduleChanges = pendingScheduleChanges.size > 0;
    
    if (!hasAlertChanges && !hasThresholdChanges && !hasScheduleChanges) {
      setSaveMessage({ type: 'info', text: 'No changes to save.' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setSavingAll(true);
    setSaveMessage(null);
    const errors = [];

    try {
      const savePromises = [];

      // Save alert settings changes
      if (hasAlertChanges) {
        const changesBySite = new Map();
        pendingChanges.forEach((change, key) => {
          if (!changesBySite.has(change.siteId)) {
            changesBySite.set(change.siteId, []);
          }
          changesBySite.get(change.siteId).push(change);
        });

        const alertPromises = Array.from(changesBySite.entries()).map(async ([siteId, changes]) => {
          try {
            const siteSettings = settings.find(s => s.siteId === siteId);
            if (!siteSettings) return;

            const settingsToSave = { ...siteSettings };
            delete settingsToSave.siteId;
            delete settingsToSave.siteName;
            delete settingsToSave.siteIp;

            changes.forEach(change => {
              settingsToSave[change.alertKey] = change.newValue;
            });

            await updateAlertSettings(siteId, settingsToSave, authData?.fullName || authData?.userName || null);
          } catch (err) {
            console.error(`Failed to save alert settings for site ${siteId}:`, err);
            errors.push(`Alert settings for Site ${siteId}`);
          }
        });

        savePromises.push(...alertPromises);
      }

      // Save threshold changes
      if (hasThresholdChanges) {
        const thresholdChangesBySite = new Map();
        pendingThresholdChanges.forEach((change, key) => {
          if (!thresholdChangesBySite.has(change.siteId)) {
            thresholdChangesBySite.set(change.siteId, {});
          }
          const siteThresholds = thresholdChangesBySite.get(change.siteId);
          if (!siteThresholds[change.metricKey]) {
            siteThresholds[change.metricKey] = {};
          }
          siteThresholds[change.metricKey][change.type] = change.value;
        });

        const thresholdPromises = Array.from(thresholdChangesBySite.entries()).map(async ([siteId, thresholdUpdates]) => {
          try {
            const siteThresholds = thresholds.find(t => t.siteId === siteId);
            if (!siteThresholds) return;

            const thresholdsToSave = { ...siteThresholds };
            delete thresholdsToSave.siteId;
            delete thresholdsToSave.siteName;
            delete thresholdsToSave.siteIp;

            // Apply all threshold changes for this site
            Object.keys(thresholdUpdates).forEach(metricKey => {
              if (!thresholdsToSave[metricKey]) {
                thresholdsToSave[metricKey] = {};
              }
              Object.assign(thresholdsToSave[metricKey], thresholdUpdates[metricKey]);
            });

            await updateThresholds(siteId, thresholdsToSave);
          } catch (err) {
            console.error(`Failed to save thresholds for site ${siteId}:`, err);
            errors.push(`Thresholds for Site ${siteId}`);
          }
        });

        savePromises.push(...thresholdPromises);
      }

      // Save schedule changes if any
      if (pendingScheduleChanges.size > 0) {
        const schedulePromises = Array.from(pendingScheduleChanges.values()).map(async (change) => {
          try {
            const siteId = change.siteId;
            
            // Get the latest settings from state (use functional update to ensure we have latest)
            const siteSettings = settings.find(s => s.siteId === siteId);
            if (!siteSettings) {
              throw new Error(`Site ${siteId} not found`);
            }

            // Use the schedule from the current state (which has been updated optimistically)
            // This ensures we have the latest schedule even if multiple changes were made
            const updatedSchedule = siteSettings.loadAverageSchedule || change.newSchedule;
            
            console.log(`Saving schedule for site ${siteId}:`, JSON.stringify(updatedSchedule, null, 2));
            
            // Prepare alert settings with updated schedule
            // Copy all settings except the ones we don't want to save
            const settingsToSave = {
              cpu: siteSettings.cpu,
              memory: siteSettings.memory,
              disk: siteSettings.disk,
              responseTime: siteSettings.responseTime,
              loadAverage: siteSettings.loadAverage,
              loadAverageSchedule: updatedSchedule,
              temperature: siteSettings.temperature,
              unrecognizedExits: siteSettings.unrecognizedExits,
              heartbeat: siteSettings.heartbeat,
              siteIpChanged: siteSettings.siteIpChanged,
              camera: siteSettings.camera,
              gate: siteSettings.gate,
              controller: siteSettings.controller,
              myparkServer: siteSettings.myparkServer,
              slackEnabled: siteSettings.slackEnabled,
              dbAvailability: siteSettings.dbAvailability === true,
              dbAvailabilityWarningDays: siteSettings.dbAvailabilityWarningDays ?? 30,
              dbAvailabilityCriticalDays: siteSettings.dbAvailabilityCriticalDays ?? 5,
              windowsFirewall: siteSettings.windowsFirewall !== false,
              pinpadStatus: siteSettings.pinpadStatus !== false,
              lastPayment: siteSettings.lastPayment !== false
            };

            const result = await updateAlertSettings(siteId, settingsToSave, authData?.fullName || authData?.userName || null);
            console.log(`Schedule save result for site ${siteId}:`, result);
          } catch (err) {
            console.error(`Failed to save schedule for site ${change.siteId}:`, err);
            console.error('Error details:', err.response?.data || err.message);
            errors.push(`Schedule for Site ${change.siteId}`);
          }
        });

        savePromises.push(...schedulePromises);
      }

      await Promise.all(savePromises);

      if (errors.length > 0) {
        setSaveMessage({ type: 'error', text: `Failed to save: ${errors.join(', ')}. Please try again.` });
      } else {
        // Clear pending changes on success
        setPendingChanges(new Map());
        setPendingThresholdChanges(new Map());
        setPendingScheduleChanges(new Map());
        setSaveMessage({ type: 'success', text: 'All changes saved successfully!' });
        // Reload data to get latest from server
        await loadSettings();
        if (activeTab === 'thresholds') {
          await loadThresholds();
        }
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      console.error('Failed to save changes:', err);
      setSaveMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSavingAll(false);
    }
  };


  // Filter settings based on search query
  const filteredSettings = settings.filter(site => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    return (
      site.siteName?.toLowerCase().includes(query) ||
      site.siteId?.toString().includes(query) ||
      site.siteIp?.toLowerCase().includes(query)
    );
  });

  if (loading && settings.length === 0) {
    return (
      <div className="alert-settings-overview">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error && settings.length === 0) {
    return (
      <div className="alert-settings-overview">
        <div className="error-message">{error}</div>
        <button onClick={loadSettings} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="alert-settings-overview">
      <header className="page-header">
        <div className="header-content">
          <h1>Alert Settings Overview</h1>
          <div className="header-actions">
            <button onClick={() => navigate('/')} className="nav-button">
              Back to Dashboard
            </button>
            <button onClick={() => navigate('/alerts-history')} className="nav-button">
              Alerts History
            </button>
            <button onClick={loadSettings} className="refresh-button" title="Refresh">
              ↻
            </button>
            <button 
              onClick={handleSaveAll} 
              className="save-button"
              disabled={(pendingChanges.size === 0 && pendingThresholdChanges.size === 0 && pendingScheduleChanges.size === 0) || savingAll}
            >
              {savingAll ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </header>

      <div className="page-content">
        {saveMessage && (
          <div className={`save-message save-message-${saveMessage.type}`}>
            {saveMessage.text}
          </div>
        )}
        
        {/* Tabs */}
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'alerts' ? 'active' : ''}`}
            onClick={() => setActiveTab('alerts')}
          >
            Alert Settings
          </button>
          <button
            className={`tab-button ${activeTab === 'thresholds' ? 'active' : ''}`}
            onClick={() => setActiveTab('thresholds')}
          >
            Thresholds
          </button>
          <button
            className={`tab-button ${activeTab === 'schedule' ? 'active' : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            Load Average Schedule
          </button>
        </div>

        {activeTab === 'alerts' && (
          <>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by site name, ID, or IP..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="settings-container">
          {/* Desktop Table View */}
          <div className="desktop-view">
            <div className="table-wrapper">
              <table className="settings-table">
                <thead>
                  <tr>
                    <th className="site-id-col">Site ID</th>
                    <th className="site-name-col">Site Name</th>
                    {alertTypes.map(alert => (
                      <th key={alert.key} className="alert-col">
                        <div className="alert-header">
                          <span>{alert.label}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSettings.length === 0 ? (
                    <tr>
                      <td colSpan={alertTypes.length + 2} className="no-results">
                        {searchQuery ? 'No sites found matching your search.' : 'No monitored sites found.'}
                      </td>
                    </tr>
                  ) : (
                    filteredSettings.map(site => (
                      <tr key={site.siteId}>
                        <td className="site-id-col">
                          <span className="site-id">{site.siteId}</span>
                        </td>
                        <td className="site-name-col">
                          <div className="site-name">{site.siteName || `Site ${site.siteId}`}</div>
                          {site.siteIp && <div className="site-ip">{site.siteIp}</div>}
                        </td>
                        {alertTypes.map(alert => {
                          const savingKey = `${site.siteId}-${alert.key}`;
                          const isSaving = saving.has(savingKey);
                          return (
                            <td key={alert.key} className="alert-col">
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={site[alert.key] === true}
                                  onChange={() => handleToggle(site.siteId, alert.key)}
                                  disabled={isSaving}
                                />
                                <span className={`slider ${isSaving ? 'saving' : ''}`}>
                                  {isSaving && <span className="saving-indicator">...</span>}
                                </span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-view">
            {filteredSettings.length === 0 ? (
              <div className="no-results-card">
                {searchQuery ? 'No sites found matching your search.' : 'No monitored sites found.'}
              </div>
            ) : (
              filteredSettings.map(site => (
                <div key={site.siteId} className="site-card">
                  <div className="site-card-header">
                    <div className="site-card-id">
                      <span className="site-id-label">Site ID</span>
                      <span className="site-id-value">{site.siteId}</span>
                    </div>
                    <div className="site-card-name">
                      <span className="site-name-label">Site Name</span>
                      <div className="site-name-value">{site.siteName || `Site ${site.siteId}`}</div>
                      {site.siteIp && <div className="site-ip-value">{site.siteIp}</div>}
                    </div>
                  </div>
                  <div className="site-card-alerts">
                    {alertTypes.map(alert => {
                      const savingKey = `${site.siteId}-${alert.key}`;
                      const isSaving = saving.has(savingKey);
                      return (
                        <div key={alert.key} className="alert-row">
                          <label className="alert-label">{alert.label}</label>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={site[alert.key] === true}
                              onChange={() => handleToggle(site.siteId, alert.key)}
                              disabled={isSaving}
                            />
                            <span className={`slider ${isSaving ? 'saving' : ''}`}>
                              {isSaving && <span className="saving-indicator">...</span>}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

            <div className="info-footer">
              <p>Showing {filteredSettings.length} of {settings.length} monitored site(s)</p>
            </div>
          </>
        )}

        {activeTab === 'thresholds' && (
          <ThresholdsView 
            thresholds={thresholds}
            setThresholds={setThresholds}
            settings={settings}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            loading={loadingThresholds}
            pendingThresholdChanges={pendingThresholdChanges}
            setPendingThresholdChanges={setPendingThresholdChanges}
            onRefresh={loadThresholds}
          />
        )}

        {activeTab === 'schedule' && (
          <LoadAverageScheduleView 
            settings={settings}
            setSettings={setSettings}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            pendingScheduleChanges={pendingScheduleChanges}
            setPendingScheduleChanges={setPendingScheduleChanges}
          />
        )}
      </div>
    </div>
  );
};

// Thresholds View Component
const ThresholdsView = ({ thresholds, setThresholds, settings, searchQuery, setSearchQuery, loading, pendingThresholdChanges, setPendingThresholdChanges, onRefresh }) => {
  const metricTypes = [
    { key: 'cpu', label: 'CPU', unit: '%' },
    { key: 'memory', label: 'Memory', unit: '%' },
    { key: 'disk', label: 'Disk', unit: '%' },
    { key: 'responseTime', label: 'Response Time', unit: 'ms' },
    { key: 'loadAverage', label: 'Load Average', unit: '' },
    { key: 'temperature', label: 'Temperature', unit: '°C' },
    { key: 'unrecognizedExits', label: 'Unrecognized Exits', unit: '' },
    { key: 'lastPayment', label: 'Last Payment', unit: 'min' }
  ];

  // Filter thresholds based on search query
  const filteredThresholds = thresholds.filter(threshold => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    return (
      threshold.siteName?.toLowerCase().includes(query) ||
      threshold.siteId?.toString().includes(query) ||
      threshold.siteIp?.toLowerCase().includes(query)
    );
  });

  return (
    <>
      {loading && (
        <div className="loading-spinner" style={{ marginBottom: '1rem' }}>Loading thresholds...</div>
      )}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by site name, ID, or IP..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="settings-container">
        {/* Desktop Table View */}
        <div className="desktop-view">
          <div className="table-wrapper">
            <table className="settings-table">
              <thead>
                <tr>
                  <th className="site-id-col">Site ID</th>
                  <th className="site-name-col">Site Name</th>
                  {metricTypes.map(metric => (
                    <th key={metric.key} className="metric-col">
                      <div className="metric-header">
                        <div>{metric.label}</div>
                        <div className="metric-subheader">Warning / Critical</div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredThresholds.length === 0 ? (
                  <tr>
                    <td colSpan={metricTypes.length + 2} className="no-results">
                      {searchQuery ? 'No sites found matching your search.' : 'No thresholds found.'}
                    </td>
                  </tr>
                ) : (
                  filteredThresholds.map(threshold => (
                    <tr key={threshold.siteId}>
                      <td className="site-id-col">
                        <span className="site-id">{threshold.siteId}</span>
                      </td>
                      <td className="site-name-col">
                        <div className="site-name">{threshold.siteName || `Site ${threshold.siteId}`}</div>
                        {threshold.siteIp && <div className="site-ip">{threshold.siteIp}</div>}
                      </td>
                      {metricTypes.map(metric => {
                        const metricData = threshold[metric.key];
                        const originalWarning = metricData?.warning ?? null;
                        const originalCritical = metricData?.critical ?? null;
                        
                        // Check if there are pending changes
                        const warningChangeKey = `${threshold.siteId}-${metric.key}-warning`;
                        const criticalChangeKey = `${threshold.siteId}-${metric.key}-critical`;
                        const warningChange = pendingThresholdChanges.get(warningChangeKey);
                        const criticalChange = pendingThresholdChanges.get(criticalChangeKey);
                        
                        const warning = warningChange?.value ?? originalWarning ?? 'N/A';
                        const critical = criticalChange?.value ?? originalCritical ?? 'N/A';
                        
                        return (
                          <td key={metric.key} className="metric-col">
                            <div className="threshold-values">
                              <input
                                type="number"
                                className="threshold-input threshold-warning"
                                value={warning === 'N/A' ? '' : warning}
                                onChange={(e) => {
                                  const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                                  setThresholds(prev => prev.map(t => 
                                    t.siteId === threshold.siteId 
                                      ? { ...t, [metric.key]: { ...t[metric.key], warning: newValue } }
                                      : t
                                  ));
                                  
                                  const changeKey = `${threshold.siteId}-${metric.key}-warning`;
                                  if (newValue === null || newValue === originalWarning) {
                                    setPendingThresholdChanges(prev => {
                                      const newMap = new Map(prev);
                                      newMap.delete(changeKey);
                                      return newMap;
                                    });
                                  } else {
                                    setPendingThresholdChanges(prev => new Map(prev).set(changeKey, {
                                      siteId: threshold.siteId,
                                      metricKey: metric.key,
                                      type: 'warning',
                                      value: newValue,
                                      originalValue: originalWarning
                                    }));
                                  }
                                }}
                                placeholder="N/A"
                                step={metric.key === 'responseTime' || metric.key === 'unrecognizedExits' || metric.key === 'lastPayment' ? '1' : '0.1'}
                                min="0"
                              />
                              <span className="threshold-separator">/</span>
                              <input
                                type="number"
                                className="threshold-input threshold-critical"
                                value={critical === 'N/A' ? '' : critical}
                                onChange={(e) => {
                                  const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                                  setThresholds(prev => prev.map(t => 
                                    t.siteId === threshold.siteId 
                                      ? { ...t, [metric.key]: { ...t[metric.key], critical: newValue } }
                                      : t
                                  ));
                                  
                                  const changeKey = `${threshold.siteId}-${metric.key}-critical`;
                                  if (newValue === null || newValue === originalCritical) {
                                    setPendingThresholdChanges(prev => {
                                      const newMap = new Map(prev);
                                      newMap.delete(changeKey);
                                      return newMap;
                                    });
                                  } else {
                                    setPendingThresholdChanges(prev => new Map(prev).set(changeKey, {
                                      siteId: threshold.siteId,
                                      metricKey: metric.key,
                                      type: 'critical',
                                      value: newValue,
                                      originalValue: originalCritical
                                    }));
                                  }
                                }}
                                placeholder="N/A"
                                step={metric.key === 'responseTime' || metric.key === 'unrecognizedExits' || metric.key === 'lastPayment' ? '1' : '0.1'}
                                min="0"
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="mobile-view">
          {filteredThresholds.length === 0 ? (
            <div className="no-results-card">
              {searchQuery ? 'No sites found matching your search.' : 'No thresholds found.'}
            </div>
          ) : (
            filteredThresholds.map(threshold => (
              <div key={threshold.siteId} className="site-card">
                <div className="site-card-header">
                  <div className="site-card-id">
                    <span className="site-id-label">Site ID</span>
                    <span className="site-id-value">{threshold.siteId}</span>
                  </div>
                  <div className="site-card-name">
                    <span className="site-name-label">Site Name</span>
                    <div className="site-name-value">{threshold.siteName || `Site ${threshold.siteId}`}</div>
                    {threshold.siteIp && <div className="site-ip-value">{threshold.siteIp}</div>}
                  </div>
                </div>
                <div className="thresholds-grid">
                  {metricTypes.map(metric => {
                    const metricData = threshold[metric.key];
                    const originalWarning = metricData?.warning ?? null;
                    const originalCritical = metricData?.critical ?? null;
                    
                    // Check if there are pending changes
                    const warningChangeKey = `${threshold.siteId}-${metric.key}-warning`;
                    const criticalChangeKey = `${threshold.siteId}-${metric.key}-critical`;
                    const warningChange = pendingThresholdChanges.get(warningChangeKey);
                    const criticalChange = pendingThresholdChanges.get(criticalChangeKey);
                    
                    const warning = warningChange?.value ?? originalWarning ?? 'N/A';
                    const critical = criticalChange?.value ?? originalCritical ?? 'N/A';
                    
                    // Get unit label for the metric
                    const getUnitLabel = () => {
                      if (metric.key === 'responseTime') return 'ms';
                      if (metric.key === 'loadAverage') return '';
                      if (metric.key === 'unrecognizedExits') return '';
                      if (metric.key === 'lastPayment') return 'min';
                      if (metric.key === 'temperature') return '°C';
                      return '%';
                    };

                    const unitLabel = getUnitLabel();
                    const warningLabel = unitLabel ? `Warning (${unitLabel})` : 'Warning';
                    const criticalLabel = unitLabel ? `Critical (${unitLabel})` : 'Critical';

                    // Get section title for the metric
                    const getSectionTitle = () => {
                      if (metric.key === 'responseTime') return 'Response Time Thresholds';
                      if (metric.key === 'loadAverage') return 'Load Average Thresholds';
                      if (metric.key === 'unrecognizedExits') return 'Unrecognized Exits Thresholds';
                      if (metric.key === 'lastPayment') return 'Last Payment Thresholds';
                      if (metric.key === 'temperature') return 'Temperature Thresholds';
                      if (metric.key === 'cpu') return 'CPU Thresholds';
                      if (metric.key === 'memory') return 'Memory Thresholds';
                      if (metric.key === 'disk') return 'Disk Space Thresholds';
                      return `${metric.label} Thresholds`;
                    };

                    // Get description/hint for specific metrics
                    const getDescription = () => {
                      return null;
                    };

                    const description = getDescription();

                    return (
                      <div key={metric.key} className="threshold-section-mobile">
                        <div className="threshold-section-title">
                          <span className="threshold-section-icon">●</span>
                          <div>
                            <div>{getSectionTitle()}</div>
                            {description && (
                              <div className="threshold-section-description">{description}</div>
                            )}
                          </div>
                        </div>
                        <div className="threshold-values-mobile">
                          <div className="threshold-input-wrapper">
                            <label className="threshold-input-label">{warningLabel}</label>
                            <input
                              type="number"
                              className="threshold-input threshold-warning"
                              value={warning === 'N/A' ? '' : warning}
                              onChange={(e) => {
                                const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                                setThresholds(prev => prev.map(t => 
                                  t.siteId === threshold.siteId 
                                    ? { ...t, [metric.key]: { ...t[metric.key], warning: newValue } }
                                    : t
                                ));
                                
                                const changeKey = `${threshold.siteId}-${metric.key}-warning`;
                                if (newValue === null || newValue === originalWarning) {
                                  setPendingThresholdChanges(prev => {
                                    const newMap = new Map(prev);
                                    newMap.delete(changeKey);
                                    return newMap;
                                  });
                                } else {
                                  setPendingThresholdChanges(prev => new Map(prev).set(changeKey, {
                                    siteId: threshold.siteId,
                                    metricKey: metric.key,
                                    type: 'warning',
                                    value: newValue,
                                    originalValue: originalWarning
                                  }));
                                }
                              }}
                              placeholder="N/A"
                              step={metric.key === 'responseTime' || metric.key === 'unrecognizedExits' || metric.key === 'lastPayment' ? '1' : '0.1'}
                              min="0"
                            />
                          </div>
                          <div className="threshold-input-wrapper">
                            <label className="threshold-input-label">{criticalLabel}</label>
                            <input
                              type="number"
                              className="threshold-input threshold-critical"
                              value={critical === 'N/A' ? '' : critical}
                              onChange={(e) => {
                                const newValue = e.target.value === '' ? null : parseFloat(e.target.value);
                                setThresholds(prev => prev.map(t => 
                                  t.siteId === threshold.siteId 
                                    ? { ...t, [metric.key]: { ...t[metric.key], critical: newValue } }
                                    : t
                                ));
                                
                                const changeKey = `${threshold.siteId}-${metric.key}-critical`;
                                if (newValue === null || newValue === originalCritical) {
                                  setPendingThresholdChanges(prev => {
                                    const newMap = new Map(prev);
                                    newMap.delete(changeKey);
                                    return newMap;
                                  });
                                } else {
                                  setPendingThresholdChanges(prev => new Map(prev).set(changeKey, {
                                    siteId: threshold.siteId,
                                    metricKey: metric.key,
                                    type: 'critical',
                                    value: newValue,
                                    originalValue: originalCritical
                                  }));
                                }
                              }}
                              placeholder="N/A"
                              step={metric.key === 'responseTime' || metric.key === 'unrecognizedExits' || metric.key === 'lastPayment' ? '1' : '0.1'}
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="info-footer">
        <p>Showing {filteredThresholds.length} of {thresholds.length} monitored site(s)</p>
      </div>
    </>
  );
};

// Load Average Schedule View Component
const LoadAverageScheduleView = ({ settings, setSettings, searchQuery, setSearchQuery, pendingScheduleChanges, setPendingScheduleChanges }) => {
  const daysOfWeek = [
    { key: 'sunday', label: 'Sunday' },
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' }
  ];

  // Get default schedule structure
  const getDefaultDaySchedule = () => ({
    enabled: false,
    timeSlots: [{ start: "00:00", end: "23:59" }]
  });

  // Get schedule for a site and day (with pending changes applied)
  const getScheduleForDay = (site, dayKey) => {
    const changeKey = `${site.siteId}-${dayKey}`;
    const pendingChange = pendingScheduleChanges.get(changeKey);
    
    if (pendingChange) {
      return pendingChange.newSchedule;
    }

    const schedule = site.loadAverageSchedule || {};
    const daySchedule = schedule[dayKey];
    
    if (!daySchedule) {
      return getDefaultDaySchedule();
    }

    return daySchedule;
  };

  // Handle day toggle (enable/disable)
  const handleDayToggle = (siteId, dayKey) => {
    const site = settings.find(s => s.siteId === siteId);
    if (!site) return;

    const currentSchedule = getScheduleForDay(site, dayKey);
    const newSchedule = {
      ...currentSchedule,
      enabled: !currentSchedule.enabled
    };

    updateScheduleForDay(siteId, dayKey, newSchedule, site);
  };

  // Handle time slot change
  const handleTimeSlotChange = (siteId, dayKey, slotIndex, field, value) => {
    const site = settings.find(s => s.siteId === siteId);
    if (!site) return;

    const currentSchedule = getScheduleForDay(site, dayKey);
    const newTimeSlots = [...currentSchedule.timeSlots];
    newTimeSlots[slotIndex] = {
      ...newTimeSlots[slotIndex],
      [field]: value
    };

    const newSchedule = {
      ...currentSchedule,
      timeSlots: newTimeSlots
    };

    updateScheduleForDay(siteId, dayKey, newSchedule, site);
  };

  // Add time slot
  const addTimeSlot = (siteId, dayKey) => {
    const site = settings.find(s => s.siteId === siteId);
    if (!site) return;

    const currentSchedule = getScheduleForDay(site, dayKey);
    const newTimeSlots = [...currentSchedule.timeSlots, { start: "00:00", end: "23:59" }];
    const newSchedule = {
      ...currentSchedule,
      enabled: true, // Enable day when adding time slot
      timeSlots: newTimeSlots
    };

    updateScheduleForDay(siteId, dayKey, newSchedule, site);
  };

  // Remove time slot
  const removeTimeSlot = (siteId, dayKey, slotIndex) => {
    const site = settings.find(s => s.siteId === siteId);
    if (!site) return;

    const currentSchedule = getScheduleForDay(site, dayKey);
    const newTimeSlots = currentSchedule.timeSlots.filter((_, idx) => idx !== slotIndex);
    const newSchedule = {
      ...currentSchedule,
      timeSlots: newTimeSlots.length > 0 ? newTimeSlots : [{ start: "00:00", end: "23:59" }]
    };

    updateScheduleForDay(siteId, dayKey, newSchedule, site);
  };

  // Update schedule for a day and track changes
  const updateScheduleForDay = (siteId, dayKey, newSchedule, site) => {
    // Get original schedule from the site object
    const originalSchedule = site.loadAverageSchedule || {};
    const originalDaySchedule = originalSchedule[dayKey] || getDefaultDaySchedule();

    // Build the updated full schedule
    const updatedFullSchedule = {
      ...originalSchedule,
      [dayKey]: newSchedule
    };

    // Optimistically update UI
    setSettings(prev => prev.map(s => {
      if (s.siteId !== siteId) return s;
      return {
        ...s,
        loadAverageSchedule: updatedFullSchedule
      };
    }));

    // Track pending changes - use siteId as key to track full schedule
    const changeKey = `${siteId}`;
    
    // Check if full schedule matches original
    const isChanged = JSON.stringify(updatedFullSchedule) !== JSON.stringify(originalSchedule);
    
    if (!isChanged) {
      // Remove from pending changes if it matches original
      setPendingScheduleChanges(prev => {
        const newMap = new Map(prev);
        newMap.delete(changeKey);
        return newMap;
      });
    } else {
      // Add to pending changes
      setPendingScheduleChanges(prev => {
        const newMap = new Map(prev);
        newMap.set(changeKey, {
          siteId,
          newSchedule: updatedFullSchedule,
          originalSchedule: originalSchedule
        });
        return newMap;
      });
    }
  };

  // Filter settings based on search query
  const filteredSettings = settings.filter(site => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    return (
      site.siteName?.toLowerCase().includes(query) ||
      site.siteId?.toString().includes(query) ||
      site.siteIp?.toLowerCase().includes(query)
    );
  });

  return (
    <>
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by site name, ID, or IP..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="settings-container">
        {/* Desktop Table View */}
        <div className="desktop-view">
          <div className="table-wrapper">
            <table className="settings-table">
              <thead>
                <tr>
                  <th className="site-id-col">Site ID</th>
                  <th className="site-name-col">Site Name</th>
                  {daysOfWeek.map(day => (
                    <th key={day.key} className="schedule-day-col">
                      <div className="schedule-header">
                        {day.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSettings.length === 0 ? (
                  <tr>
                    <td colSpan={daysOfWeek.length + 2} className="no-results">
                      {searchQuery ? 'No sites found matching your search.' : 'No monitored sites found.'}
                    </td>
                  </tr>
                ) : (
                  filteredSettings.map(site => (
                    <tr key={site.siteId}>
                      <td className="site-id-col">
                        <span className="site-id">{site.siteId}</span>
                      </td>
                      <td className="site-name-col">
                        <div className="site-name">{site.siteName || `Site ${site.siteId}`}</div>
                        {site.siteIp && <div className="site-ip">{site.siteIp}</div>}
                      </td>
                      {daysOfWeek.map(day => {
                        const daySchedule = getScheduleForDay(site, day.key);
                        return (
                          <td key={day.key} className="schedule-day-col">
                            <div className="schedule-editor">
                              <label className="schedule-toggle">
                                <input
                                  type="checkbox"
                                  checked={daySchedule.enabled}
                                  onChange={() => handleDayToggle(site.siteId, day.key)}
                                />
                                <span className="toggle-label">{daySchedule.enabled ? 'On' : 'Off'}</span>
                              </label>
                              {daySchedule.enabled && (
                                <div className="time-slots">
                                  {daySchedule.timeSlots.map((slot, slotIndex) => (
                                    <div key={slotIndex} className="time-slot-row">
                                      <input
                                        type="time"
                                        className="time-input"
                                        value={slot.start}
                                        onChange={(e) => handleTimeSlotChange(site.siteId, day.key, slotIndex, 'start', e.target.value)}
                                      />
                                      <span className="time-separator">-</span>
                                      <input
                                        type="time"
                                        className="time-input"
                                        value={slot.end}
                                        onChange={(e) => handleTimeSlotChange(site.siteId, day.key, slotIndex, 'end', e.target.value)}
                                      />
                                      {daySchedule.timeSlots.length > 1 && (
                                        <button
                                          type="button"
                                          className="remove-slot-btn"
                                          onClick={() => removeTimeSlot(site.siteId, day.key, slotIndex)}
                                          title="Remove time slot"
                                        >
                                          ×
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    className="add-slot-btn"
                                    onClick={() => addTimeSlot(site.siteId, day.key)}
                                    title="Add time slot"
                                  >
                                    + Add
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="mobile-view">
          {filteredSettings.length === 0 ? (
            <div className="no-results-card">
              {searchQuery ? 'No sites found matching your search.' : 'No monitored sites found.'}
            </div>
          ) : (
            filteredSettings.map(site => {
              return (
                <div key={site.siteId} className="site-card">
                  <div className="site-card-header">
                    <div className="site-card-id">
                      <span className="site-id-label">SITE ID</span>
                      <span className="site-id-value">{site.siteId}</span>
                    </div>
                    <div className="site-card-name">
                      <span className="site-name-label">SITE NAME</span>
                      <div className="site-name-value">{site.siteName || `Site ${site.siteId}`}</div>
                      {site.siteIp && <div className="site-ip-value">{site.siteIp}</div>}
                    </div>
                  </div>
                  <div className="schedule-grid">
                    {daysOfWeek.map(day => {
                      const daySchedule = getScheduleForDay(site, day.key);
                      return (
                        <div key={day.key} className="schedule-row-mobile">
                          <div className="schedule-row-header">
                            <div className="schedule-label">{day.label}</div>
                            <label className="schedule-toggle">
                              <input
                                type="checkbox"
                                checked={daySchedule.enabled}
                                onChange={() => handleDayToggle(site.siteId, day.key)}
                              />
                              <span className="toggle-label">{daySchedule.enabled ? 'On' : 'Off'}</span>
                            </label>
                          </div>
                          {daySchedule.enabled && (
                            <div className="time-slots-mobile">
                              {daySchedule.timeSlots.map((slot, slotIndex) => (
                                <div key={slotIndex} className="time-slot-row">
                                  <input
                                    type="time"
                                    className="time-input"
                                    value={slot.start}
                                    onChange={(e) => handleTimeSlotChange(site.siteId, day.key, slotIndex, 'start', e.target.value)}
                                  />
                                  <span className="time-separator">-</span>
                                  <input
                                    type="time"
                                    className="time-input"
                                    value={slot.end}
                                    onChange={(e) => handleTimeSlotChange(site.siteId, day.key, slotIndex, 'end', e.target.value)}
                                  />
                                  {daySchedule.timeSlots.length > 1 && (
                                    <button
                                      type="button"
                                      className="remove-slot-btn"
                                      onClick={() => removeTimeSlot(site.siteId, day.key, slotIndex)}
                                      title="Remove time slot"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                              <button
                                type="button"
                                className="add-slot-btn"
                                onClick={() => addTimeSlot(site.siteId, day.key)}
                                title="Add time slot"
                              >
                                + Add Time Slot
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="info-footer">
        <p>Showing {filteredSettings.length} of {settings.length} monitored site(s)</p>
      </div>
    </>
  );
};

export default AlertSettingsOverview;

